import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { 
  Grid, 
  TextField, 
  Button, 
  Card, 
  CardContent, 
  Typography, 
  Alert, 
  CircularProgress,
  MenuItem,
  Select,
  InputLabel,
  FormControl
} from "@mui/material";
import { fetchOrder, createOrder, updateOrder } from "../../Service/Order.service";
import "./OrderForm.css";
import HeaderTop from "../HeaderTop/HeaderTop";

const OrderForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(!!id);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formData, setFormData] = useState({
    company_name: "",
    customer_name: "",
    phone: "",
    email: "",
    delivery_date: "",
    red_injera_quantity: 0,
    white_injera_quantity: 0,
    packaging_type: "plastic",
    special_instructions: "",
    delivery_location: "",
    address: "",
    city: "",
    order_status: "pending",
    total_price: 0,
  });

  useEffect(() => {
    if (id) {
      const loadOrder = async () => {
        try {
          const order = await fetchOrder(id);
          setFormData({
            ...order,
            delivery_date: order.delivery_date.split("T")[0],
          });
          setLoading(false);
        } catch (err) {
          setError(err.message);
          setLoading(false);
        }
      };
      loadOrder();
    }
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: parseInt(value) || 0,
    }));
  };

  const calculateTotal = () => {
    const redPrice = formData.red_injera_quantity * 2;
    const whitePrice = formData.white_injera_quantity * 1.5;
    return redPrice + whitePrice;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const orderData = id ? {
        ...formData,
        total_price: calculateTotal(),
      } : {
        ...formData,
        order_status: "pending",
        total_price: 0,
      };

      setLoading(true);
      if (id) {
        await updateOrder(id, orderData);
      } else {
        await createOrder(orderData);
      }
      
      setSuccess("Order submitted successfully!");
      
      setTimeout(() => {
        setSuccess(null);
        navigate("/d2f4b9a1-7c38-4d7e-ae1e-938b33d8b75e34rnbndfg");
      }, 1000);
      
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  if (loading && !error) {
    return (
      <Grid className="loadingOverlay">
        <CircularProgress color="primary" />
        <Typography>Loading order data...</Typography>
      </Grid>
    );
  }

  return (
    <>
    <HeaderTop/>
    <Grid className="orderFormWrapper">
      <Grid className="orderFormContainer">
        <Card className="formCard">
          <CardContent>
            <Typography variant="h4" component="h2" className="formTitle">
              {id ? "Edit Order" : "Create New Order"}
            </Typography>
            <Typography className="formSubtitle">
              {id ? "Update the order details below" : "Fill in the form below to create a new order"}
            </Typography>

            {error && (
              <Alert severity="error" className="errorMessage" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" className="successMessage" onClose={() => setSuccess(null)}>
                {success}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Customer Name *"
                    name="customer_name"
                    value={formData.customer_name}
                    onChange={handleChange}
                    required
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone *"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Delivery Date *"
                    name="delivery_date"
                    type="date"
                    value={formData.delivery_date}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Delivery Location"
                    name="delivery_location"
                    value={formData.delivery_location}
                    onChange={handleChange}
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Red Injera Quantity"
                    name="red_injera_quantity"
                    type="number"
                    min="0"
                    value={formData.red_injera_quantity}
                    onChange={handleNumberChange}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} md={6} className="text-primary h3">
                  <TextField
                    fullWidth
                    label="White Injera Quantity"
                    name="white_injera_quantity"
                    type="number"
                    min="0"
                    value={formData.white_injera_quantity}
                    onChange={handleNumberChange}
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Packaging Type</InputLabel>
                    <Select
                      label="Packaging Type"
                      name="packaging_type"
                      value={formData.packaging_type}
                      onChange={handleChange}
                    >
                      <MenuItem value="plastic">Plastic</MenuItem>
                      <MenuItem value="paper">Paper</MenuItem>
                      <MenuItem value="cloth">Cloth</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {id && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Order Status</InputLabel>
                      <Select
                        label="Order Status"
                        name="order_status"
                        value={formData.order_status}
                        onChange={handleChange}
                      >
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="processing">Processing</MenuItem>
                        <MenuItem value="confirmed">Confirmed</MenuItem>
                        <MenuItem value="delivered">Delivered</MenuItem>
                        <MenuItem value="cancelled">Cancelled</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Special Instructions"
                    name="special_instructions"
                    multiline
                    rows={4}
                    value={formData.special_instructions}
                    onChange={handleChange}
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="City"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    variant="outlined"
                  />
                </Grid>

                <Grid item xs={12}>
                  <div className="totalPriceContainer">
                    <Typography variant="h6" className="totalLabel">
                      Total Price:
                    </Typography>
                    <Typography variant="h5" className="totalAmount">
                      ${"0.00"}
                    </Typography>
                  </div>
                </Grid>

                <Grid item xs={12}>
                  <Grid container spacing={2} justifyContent="flex-end">
                    <Grid item>
                      <Button
                        variant="outlined"
                        onClick={() => navigate("/")}
                        className="cancelButton"
                      >
                        Cancel
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={loading}
                        className="submitButton"
                      >
                        {loading ? (
                          <>
                            <CircularProgress size={24} color="inherit" />
                            <span style={{ marginLeft: 8 }}>
                              {id ? "Updating..." : "Creating..."}
                            </span>
                          </>
                        ) : id ? (
                          "Update Order"
                        ) : (
                          "Create Order"
                        )}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
    </>
  );
};

export default OrderForm;